import type { Trip, TripDestination } from '@/services/trips';
import {
  coordsWithinCountryBounds,
  getDestinationCoordinates as getKnownDestinationCoordinates,
  haversineDistance,
  resolveTripDestinationLabel,
} from '@/utils/geocodeHelpers';
import { getTripDayContext } from '@/utils/tripDayContext';
import { getDestinationCoordinates as geocodeDestinationCoordinates } from '@/services/weather';

export type TripWeatherLocation = {
  label: string;
  city: string;
  latitude: number;
  longitude: number;
  countryCode?: string;
  source: 'destination-coords' | 'trip-coords' | 'known-city' | 'geocoded';
};

const MAX_COORD_DRIFT_METERS = 200_000;

const isValidCoord = (latitude?: number, longitude?: number): latitude is number =>
  typeof latitude === 'number' &&
  typeof longitude === 'number' &&
  Number.isFinite(latitude) &&
  Number.isFinite(longitude) &&
  Math.abs(latitude) <= 90 &&
  Math.abs(longitude) <= 180 &&
  !(latitude === 0 && longitude === 0);

const coordsMatchKnownCity = (cityLabel: string, latitude: number, longitude: number): boolean => {
  const known = getKnownDestinationCoordinates(cityLabel);
  if (!known) return true;
  const distance = haversineDistance(
    { lat: latitude, lng: longitude },
    { lat: known.lat, lng: known.lng },
  );
  return distance <= MAX_COORD_DRIFT_METERS;
};

const resolveKnownCityCoordinates = (
  label: string,
): { latitude: number; longitude: number } | null => {
  const known = getKnownDestinationCoordinates(label);
  if (!known) return null;
  return { latitude: known.lat, longitude: known.lng };
};

const pickDestinationForDate = (trip: Trip, date: Date): TripDestination | null => {
  const context = getTripDayContext(trip, date);
  return context.activeDestination ?? trip.destinations?.[0] ?? null;
};

export async function resolveTripWeatherLocation(
  trip: Trip,
  date: Date = new Date(),
): Promise<TripWeatherLocation | null> {
  const destination = pickDestinationForDate(trip, date);
  const parsed = resolveTripDestinationLabel(destination, trip.destination);
  if (!parsed) return null;

  const { label, city, countryCode } = parsed;

  const candidateCoords: Array<{ latitude: number; longitude: number; source: TripWeatherLocation['source'] }> = [];

  if (destination?.coordinates && isValidCoord(destination.coordinates.latitude, destination.coordinates.longitude)) {
    candidateCoords.push({
      latitude: destination.coordinates.latitude,
      longitude: destination.coordinates.longitude,
      source: 'destination-coords',
    });
  }

  if (
    trip.destinationCoordinates &&
    isValidCoord(trip.destinationCoordinates.latitude, trip.destinationCoordinates.longitude)
  ) {
    candidateCoords.push({
      latitude: trip.destinationCoordinates.latitude,
      longitude: trip.destinationCoordinates.longitude,
      source: 'trip-coords',
    });
  }

  const known = resolveKnownCityCoordinates(label);
  if (known) {
    candidateCoords.push({ ...known, source: 'known-city' });
  }

  for (const candidate of candidateCoords) {
    const inCountry = coordsWithinCountryBounds(countryCode, candidate.latitude, candidate.longitude);
    if (!inCountry) continue;
    if (coordsMatchKnownCity(label, candidate.latitude, candidate.longitude)) {
      return {
        label,
        city,
        latitude: candidate.latitude,
        longitude: candidate.longitude,
        countryCode,
        source: candidate.source,
      };
    }
  }

  if (known && coordsWithinCountryBounds(countryCode, known.latitude, known.longitude)) {
    return {
      label,
      city,
      latitude: known.latitude,
      longitude: known.longitude,
      countryCode,
      source: 'known-city',
    };
  }

  const geocoded = await geocodeDestinationCoordinates(label, { country: countryCode });
  if (!geocoded || !isValidCoord(geocoded.latitude, geocoded.longitude)) {
    return null;
  }

  if (!coordsWithinCountryBounds(countryCode, geocoded.latitude, geocoded.longitude)) {
    return null;
  }

  return {
    label,
    city: geocoded.city || city,
    latitude: geocoded.latitude,
    longitude: geocoded.longitude,
    countryCode: countryCode || geocoded.country || undefined,
    source: 'geocoded',
  };
}
